import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import OkCancelModal from '@/shared/components/OkCancelModal';
import intlShape from '@/shared/utils/intlShape';
import api from '@/shared/api';
import config from '@/config';
import ToastrService from '@/shared/services/ToastrService';
import handleHttpError from '@/shared/services/HandleHttpError';
import { SpinnerIcon } from '@/shared/components/Icons';
import { SharepointFolderTree } from '@/modules/documents/modals/SharepointFolderTree';
import messages from '../documentMessages';

const SharepointModal = ({ show, intl, documentId, onSave, onClose }) => {
  const [sites, setSites] = useState([]);
  const [foldersLoading, setFoldersLoading] = useState(false);
  const [sitesLoading, setSitesLoading] = useState(true);
  const [selectedSiteId, setSelectedSiteId] = useState(null);
  const [drivesLoading, setDrivesLoading] = useState(false);
  const [drives, setDrives] = useState([]);
  const [selectedDriveId, setSelectedDriveId] = useState(null);
  const [selectedFolderId, setSelectedFolderId] = useState('/');

  useEffect(() => {
    setSitesLoading(true);
    api.base
      .get(`${config.appUrls.documents}/api/sharepoint/sites`)
      .then(
        x => {
          setSites(!x.data ? [] : x.data);
        },
        error => {
          handleHttpError(intl)(error);
        },
      )
      .finally(() => {
        setSitesLoading(false);
      });
  }, [intl]);

  useEffect(() => {
    setDrives([]);
    setSelectedFolderId('/');
    if (!selectedSiteId) return;

    setDrivesLoading(true);
    api.base
      .get(`${config.appUrls.documents}/api/sharepoint/sites/${selectedSiteId}/drives`)
      .then(
        x => {
          setDrives(!x.data ? [] : x.data);
        },
        error => {
          handleHttpError(intl)(error);
        },
      )
      .finally(() => {
        setDrivesLoading(false);
      });
  }, [selectedSiteId, intl]);

  const save = () =>
    api.base
      .post(
        `${config.appUrls.documents}/api/documents/${documentId}/MoveToSharepoint?driveId=${selectedDriveId}&folderId=${selectedFolderId}`,
        {},
      )
      .then(
        ({ data }) => onSave(data),
        error => {
          handleHttpError(intl)(error);
        },
      );

  const closeModal = () => {
    onClose();
  };

  if (!show) return null;

  const title = () => {
    if (!sitesLoading && !drivesLoading && !foldersLoading)
      return intl.formatMessage(messages.MoveToSharepoint);

    return (
      <>
        {intl.formatMessage(messages.MoveToSharepoint)}
        <span className="margin5L">
          <SpinnerIcon />
        </span>
      </>
    );
  };

  return (
    <OkCancelModal
      title={title()}
      show={show}
      onClose={closeModal}
      disabled={!selectedSiteId || !selectedDriveId || !selectedFolderId}
      onSave={save}
    >
      <div className="form-horizontal">
        <div>
          <div className="form-group">
            <label className="control-label col-md-3">
              <FormattedMessage {...messages.SharepointSite} />
            </label>
            <div className="col-md-9" style={{ marginTop: '7px' }}>
              <select
                disabled={sites.length < 1}
                onChange={e => setSelectedSiteId(e.target?.value)}
                className="form-control"
              >
                <option value={null} />
                {sites.map(site => (
                  <option key={site.id} value={site.id}>
                    {site.name}
                  </option>
                ))}
              </select>
            </div>
            <label className="control-label col-md-3">
              <FormattedMessage {...messages.SharepointDrive} />
            </label>
            <div className="col-md-9" style={{ marginTop: '7px' }}>
              <select
                disabled={drives.length < 1}
                onChange={e => setSelectedDriveId(e.target?.value)}
                className="form-control"
              >
                <option value={null} />
                {drives.map(drive => (
                  <option key={drive.id} value={drive.id}>
                    {drive.name}
                  </option>
                ))}
              </select>
            </div>
            <label className="control-label col-md-3">
              <FormattedMessage {...messages.Folder} />
            </label>
            <div className="col-md-9" style={{ marginTop: '7px' }}>
              <SharepointFolderTree
                setLoading={setFoldersLoading}
                onSelectedFolderChanged={setSelectedFolderId}
                siteId={selectedSiteId}
                driveId={selectedDriveId}
              />
            </div>
          </div>
        </div>
      </div>
    </OkCancelModal>
  );
};

SharepointModal.propTypes = {
  show: PropTypes.bool.isRequired,
  intl: intlShape.isRequired,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  documentId: PropTypes.string.isRequired,
};

export default SharepointModal;
